import { h, Component } from 'preact';
import pure from 'utils/pure';
import Store from 'store/store';
import * as actions from 'store/actions';
import { joinClasses } from 'utils/utils';
import { getCopy } from 'utils/copy';
import { track } from 'utils/tracking';
import Youtube from 'components/shared/youtube/youtube.js';
import SimpleVideoPlayer from 'components/shared/simpleVideoPlayer/simpleVideoPlayer.js';
import s from 'components/shared/closableTrailer/closableTrailer.sss';
import Vimeo from 'components/shared/vimeo/vimeo.js';

const isServer = typeof window === 'undefined';

export default @pure class ClosableTrailer extends Component {

	constructor(...args) {
		super(...args);

		this.closeTrailer = this.closeTrailer.bind(this);
		this.closeTrailerWithKeyboard = this.closeTrailerWithKeyboard.bind(this);
		this.trailerEnded = this.trailerEnded.bind(this);
		this.playerReady = this.playerReady.bind(this);
		this.tryToClose = this.tryToClose.bind(this);
		this.tryToRemove = this.tryToRemove.bind(this);
		this.onPlay = this.onPlay.bind(this);
		this.onPause = this.onPause.bind(this);

		this.defaultState = {
			closed: false,
			playerReady: false,
			playing: false
		};

		this.disabled = false;
		this.visible = false;
		this.$video = null;
		this.state = { ...this.defaultState };
	}

	componentWillMount() {
		// if the url has a disabled - add a force-disabled flag
		if (!isServer && location.href.match(/[?&]disableOverlay(=|&|$)/)) {
			this.disabled = true;
		}

		this.computeUpdates(this.props, this.state);
	}

	componentWillUpdate(nextProps, nextState) {
		this.computeUpdates(nextProps, nextState);
	}

	componentWillUnMount() {
		if (this.visible) {
			Store.emit('VIDEO_PLAYER_HIDE');
		}
	}

	closeTrailer() {
		this.closeTrailerVideo();
		track({ a: 'closing-trailer-closed' });
	}

	closeTrailerWithKeyboard(e) {
		if (e.key === 'Enter') {
			this.closeTrailerVideo();
			track({ a: 'closing-trailer-closed' });
		}
	}

	onPlay() {
		this.setState({ isPlaying: true });
	}
	onPause() {
		this.setState({ isPlaying: false });
	}

	tryToClose(e) {
		// Clicked on the background
		if (e && e.target === this.$wrapper) {
			this.closeTrailer(event);
		}
	}

	// NOTE : we're setting state here so could be worth putting this in `componentWillUpdate`?
	closeTrailerVideo() {
		this.setState({ closed: true });

		if (this.$video && typeof this.$video.pause === 'function') {
			this.$video.pause();
		}

		window.focus();
		if (document.activeElement) {
			document.activeElement.blur();
		}

		if (this.props.didClose) {
			this.props.didClose();
		}

		Store.emit(actions.CLOSABLE_TRAILER_CLOSED, this.props.activePage);
	}

	trailerEnded() {
		this.closeTrailerVideo();
		track({ a: 'closing-trailer-ended' });
		Store.emit(actions.HOMEPAGE_TRAILER_ENDED);
	}

	playerReady() {
		// Check if the player is ready and only show the popup once the trailer is ready
		// on old devices (or scrapers like the Google scraper) the youtube player
		// isn't supported so we shouldn't show the popup
		// as this will give soft 404's (because google thinks the whole page is just black)
		this.setState({ playerReady: true });
		Store.emit(actions.CLOSABLE_TRAILER_OPEN);
	}

	tryToRemove(e) {
		// signal to remove only once the transition has faded-out
		if (e.target !== this.base || e.propertyName !== 'visibility') {
			return;
		}
		const newStyle = getComputedStyle(e.target);
		if (newStyle.visibility === 'hidden') {
			this.setState({ remove: true });
		}
	}


	computeUpdates(props, state) {
		let disableYtPopup = false;
		try {
			disableYtPopup = localStorage.getItem('__DISABLE_YT_POPUP__');
		} catch (e) {}

		if (!disableYtPopup) {
			let newVisible = state.playerReady && !state.closed;
			if (newVisible !== this.visible) {
				this.visible = newVisible;
				Store.emit(newVisible ? 'VIDEO_PLAYER_SHOW' : 'VIDEO_PLAYER_HIDE');
			}
		}
	}


	renderTT() {
		if (!this.props.showTT) {
			return;
		}

		// Should we grab from the store if not passed down as a prop?
		let imgRoot = this.props.imgRoot || '';
		return <img src={imgRoot + '/tt.png'} class={s.tt} alt={getCopy('HOME_TITLE_TREATMENT_SEO')} />;
	}

	renderOverlayText() {
		const overlayText = this.props.overlayText;
		if (!overlayText) {
			return;
		}

		return <p class={s.overlayText}>{getCopy(overlayText, {}, overlayText)}</p>;
	}

	renderNativeVideo() {
		return (
			<SimpleVideoPlayer
				src={this.props.src}
				parentClass={joinClasses(s.videoPlayer, s.simpleVideo)}
				class={s.video}
				ref={ref => this.$video = ref}
				onVideoEnded={this.trailerEnded}
				onPlayerReady={this.playerReady}
				autoplay muted={false}
				showControls overrideHideControls
				useTracking
				isClosableTrailer
				poster={this.props.thumbSrc}
			/>
		);
	}
	renderYoutubeVideo() {
		let props = this.props;
		let client = props.client || {};
		return (
			<Youtube
				activeItem={{ ytid: props.ytid }}
				maintainAspectRatio={client.deviceSize !== 'desktop'}
				iframeClass={s.youtube}
				wrapperClass={s.wrapper}
				ref={ref => this.$video = ref}
				onPlay={this.onPlay}
				onPause={this.onPause}
				client={client}
				onVideoEnded={this.trailerEnded}
				customDesktopUI={props.customDesktopUI}
				onPause={this.onPause}
				onPlayerReady={this.playerReady}
				tracking="closing-trailer"
			/>
		);
	}
	renderVimeoVideo() {
		return (
			<Vimeo
				class={s.vimeoPlayer}
				vimeoID={this.props.vimeoID}
				onPlayerReady={this.playerReady}
				autoplay muted
				useTracking
				isClosableTrailer
			/>
		);
	}

	renderVideoElement() {
		let props = this.props;
		if (props.src) {
			return this.renderNativeVideo();
		} else if (props.ytid) {
			return this.renderYoutubeVideo();
		} else if (props.vimeoID) {
			return this.renderVimeoVideo();
		}
	}

	render(props, state) {

		const {
			closeText,
			src, ytid, vimeoId,
			overlayText,
			imgRoot, client,
			showTT,
			customDesktopUI,
			children,
			activePage,
			...remainingProps
		} = props;

		const playing = this.state.isPlaying;

		if (state.remove || this.disabled) {
			return;
		}
		return (
			<div
				class={joinClasses(s.container, this.visible && s.visible)}
				key="closableTrailer"
				data-overlay="closableTrailer"
				ref={ref => this.$wrapper = ref}
				onClick={this.tryToClose}
				onTransitionEnd={this.tryToRemove}
				{...remainingProps}
			>
				<span
					class={joinClasses(s.close, playing && s.hide)}
					onClick={this.closeTrailer}
					key="close"
					role="button"
					tabindex="0"
					onKeyDown={this.closeTrailerWithKeyboard}
				>
					{props.closeText}
				</span>
				<div class={s.content}>
					{this.renderTT()}
					{this.renderOverlayText()}
					{this.renderVideoElement()}
					{children}
				</div>
			</div>
		);
	}

}
